<template>
  <div class="form-row">
    <div
      class="text-center col-md-12 pl-2 pr-2"
      v-show="hasAnythingToShow"
    >
      <div class="map-shell">
      <div class="map-menu" ref="mapMenu">
        <button
          type="button"
          class="map-menu-toggle"
          :class="{ 'is-on': !!labelField }"
          :title="$t('Features')"
          @click="toggleMapMenu"
        >
          <i class="fa fa-bars"></i>
          <span class="map-menu-text">{{ $t("Features") }}</span>
        </button>

        <div v-if="mapMenuOpen" class="map-menu-panel">
          <button
            type="button"
            class="map-menu-item"
            @click="fitAll"
          >
            <i class="fa fa-compress"></i>
            <span>{{ $t("Frame all") }}</span>
          </button>

          <button
            type="button"
            class="map-menu-item"
            :class="{ 'is-on': !!labelField }"
            :title="$t('Label on the pin')"
            @click="labelMenuOpen = !labelMenuOpen"
          >
            <i class="fa fa-tag"></i>
            <span>{{ $t("Label") }}</span>
            <i
              class="fa map-menu-caret"
              :class="labelMenuOpen ? 'fa-angle-up' : 'fa-angle-down'"
            ></i>
          </button>

          <div v-if="labelMenuOpen" class="map-menu-sub">
            <button
              v-for="option in labelOptions"
              :key="option.field"
              type="button"
              class="map-menu-sub-item"
              :class="{ 'is-active': labelField === option.field }"
              @click="setLabelField(option.field)"
            >
              {{ $t(option.label) }}
            </button>

            <button
              v-if="labelField"
              type="button"
              class="map-menu-sub-item is-clear"
              @click="setLabelField(null)"
            >
              {{ $t("Clear") }}
            </button>
          </div>
        </div>
      </div>

      <GmapMap
        ref="mapRef"
        :center="mapCenter"
        :zoom="currentZoom"
        style="width: 100%; height: 700px"
        @zoom_changed="handleZoomChanged"
      >
        <GmapMarker
          v-if="google && addressCoordinates && addressCoordinates.lat && addressCoordinates.lng"
          :position="{ lat: Number(addressCoordinates.lat), lng: Number(addressCoordinates.lng) }"
          :icon="getAddressIcon()"
        />
        <GmapMarker
          v-for="(vehicle, index) in vehicleFleetData"
          :key="'vehicle-' + getVehicleKey(vehicle)"
          v-if="google && !isDeviceMode && vehicle.position && getVehiclePosition(vehicle)"
          :position="getVehiclePosition(vehicle)"
          :icon="getVehicleMarkerIcon(vehicle)"
          :label="getVehicleLabel(vehicle)"
          @click="setSelectedVehicleDetails(vehicle)"
        />

        <GmapMarker
          v-for="device in deviceFleet"
          :key="'device-' + device.id"
          v-if="google && isDeviceMode && getDevicePosition(device)"
          :position="getDevicePosition(device)"
          :icon="getDeviceMarkerIcon(device)"
          :label="getDeviceLabel(device)"
          @click="setSelectedDevice(device)"
        />

        <GmapMarker
          v-for="(landmark, idx) in landmarkMarkers"
          :key="'landmark-' + landmark.id + '-' + idx"
          v-if="google && landmark.destino_lat && landmark.destino_long"
          :position="{ lat: Number(landmark.destino_lat), lng: Number(landmark.destino_long) }"
          :icon="getLandmarkIcon(landmark)"
          @click="onLandmarkMarkerClick(landmark)"
        />

        <GmapInfoWindow
          v-if="showInfoWindow === 'landmark' && selectedLandmark"
          :position="{ lat: Number(selectedLandmark.destino_lat), lng: Number(selectedLandmark.destino_long) }"
          @closeclick="closeInfoWindow"
        >
          <div class="landmark-info">
            <h6 class="mb-2">{{ selectedLandmark.destino_nome || selectedLandmark.name }}</h6>
            <p class="mb-1" v-if="selectedLandmark.landmark_type && selectedLandmark.landmark_type.name">
              <span class="badge badge-info">{{ selectedLandmark.landmark_type.name }}</span>
            </p>
            <p class="mb-0 text-muted small">
              {{ Number(selectedLandmark.destino_lat).toFixed(6) }},
              {{ Number(selectedLandmark.destino_long).toFixed(6) }}
            </p>
          </div>
        </GmapInfoWindow>

        <GmapCircle
          v-if="addressCoordinates && normalizedRadius && normalizedRadius > 0"
          :key="
            'circle-' +
            normalizedRadius +
            '-' +
            addressCoordinates.lat +
            '-' +
            addressCoordinates.lng
          "
          :center="addressCoordinates"
          :radius="normalizedRadius * 1000"
          :options="getCircleOptions()"
          @bounds_changed="onCircleChanged"
        />

        <GmapInfoWindow
          v-if="showInfoWindow === 'address'"
          :position="addressCoordinates"
          @closeclick="closeInfoWindow"
        >
          <div class="address-info">
            <h6 class="mb-2">📍 {{ $t("Reference Address") }}</h6>
            <p class="mb-1">
              <strong>{{ addressCoordinates.formatted_address }}</strong>
            </p>
            <p class="mb-1" v-if="normalizedRadius">
              <span class="badge badge-primary"
                >{{ $t("Radius") }}: {{ normalizedRadius }} km</span
              >
            </p>
            <p class="mb-0 text-muted small">
              {{ addressCoordinates.lat.toFixed(6) }},
              {{ addressCoordinates.lng.toFixed(6) }}
            </p>
          </div>
        </GmapInfoWindow>
      </GmapMap>
      </div>
    </div>

    <div
      class="form-group col-md-12 pl-2 pr-2 text-center"
      v-show="!hasAnythingToShow"
    >
      <p class="badge badge-warning">{{ $t("location not specified") }}</p>
      <CurrentLocationMap />
    </div>

    <b-modal
        id="device-info-modal"
        size="lg"
        hide-footer
        :title="(selectedDevice && selectedDevice.label) || $t('Device')"
        @hidden="selectedDevice = null"
    >
      <device-info
          v-if="selectedDevice"
          :device="selectedDevice"
          @open-vehicle-devices="(vehicleId) => $emit('open-devices', vehicleId)"
      />
    </b-modal>

    <b-modal
        id="vehicle-info-modal"
        size="lg"
        hide-footer
        :title="vehicleDatalhe && vehicleDatalhe.vehicle && vehicleDatalhe.vehicle.name || $t('Vehicle')"
        @hidden="closeInfoWindow"
    >
      <vehicle-info
          v-if="selectedVehicleDetails"
          :vehicle="selectedVehicleDetails"
          :vehicleDatalhe="vehicleDatalhe"
          :enable-camera-button="enableCameraButton"
          :enable-devices-button="enableDevicesButton"
          @open-camera="(vehicleId) => $emit('open-camera', vehicleId)"
          @open-devices="(vehicleId) => $emit('open-devices', vehicleId)"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { gmapApi } from "vue2-google-maps";
import { RefreshCcwIcon } from "vue-feather-icons";
import CurrentLocationMap from "./CurrentLocationMap";
import VehicleInfo from "./VehicleInfo";
import DeviceInfo from "./DeviceInfo";

const GENERIC_PIN = require("@/assets/images/pin/map-pin-generic.svg");
const LABEL_FIELD_KEY = "mapLabelField";

export default {
  props: {
    vehicleFleet: {
      type: Array,
      default: () => [],
    },
    landmarkMarkers: {
      type: Array,
      default: () => [],
    },
    zoomMapa: {
      type: Number,
      default: 13,
    },
    selectedVehicleId: {
      type: Number,
      default: null,
    },
    addressCoordinates: {
      type: Object,
      default: null,
    },
    deviceFleet: {
      type: Array,
      default: () => [],
    },
    mapMode: {
      type: String,
      default: "vehicles",
    },
    enableDevicesButton: {
      type: Boolean,
      default: false,
    },
    enableCameraButton: {
      type: Boolean,
      default: false,
    },
    radiusKm: {
      type: [Number, String],
      default: null,
      validator(value) {
        if (value === null || value === undefined) return true;
        const num = Number(value);
        return !isNaN(num) && num > 0;
      },
    },
    autoFitMap: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      latitude: 0,
      longitude: 0,
      showInfoWindow: null,
      selectedVehicleDetails: null,
      selectedDevice: null,
      vehicleDatalhe: null,
      selectedLandmark: {},
      circleRadius: 1000,
      indexMarker: null,
      markerPosition: {
        lat: 0,
        lng: 0,
      },
      // Centro padrão de fallback (São Paulo)
      center: {
        lat: -23.6086,
        lng: -46.6663,
      },
      currentZoom: 13,
      mapViewTimer: null,
      brokenIcons: {},
      checkedIcons: {},
      iconCheckVersion: 0,
      mapCenter: {
        lat: -23.6086,
        lng: -46.6663,
      },
      hasFramedOnce: false,
      unitNames: {},
      labelField: null,
      mapMenuOpen: false,
      labelMenuOpen: false,
      labelOptions: [
        { field: "license_plate", label: "License Plate" },
        { field: "name", label: "Name" },
        { field: "unit", label: "Unit" },
      ],
    };
  },

  computed: {
    isDeviceMode() {
      return this.mapMode === "devices";
    },

    google: gmapApi,

    vehicleFleetData() {
      return this.vehicleFleet ?? [];
    },

    devicePoints() {
      return (this.deviceFleet ?? [])
        .map((device) => this.getDevicePosition(device))
        .filter(Boolean);
    },

    hasAnythingToShow() {
      if (this.addressCoordinates) {
        return true;
      }

      return this.isDeviceMode
        ? this.devicePoints.length > 0
        : this.vehicleFleetData.length > 0;
    },

    /**
     * Centro calculado dinamicamente:
     * 1. Se há addressCoordinates, usa ele
     * 2. Se há pins (veículos + landmarks), calcula o centróide
     * 3. Fallback: São Paulo
     */
    computedCenter() {
      if (this.addressCoordinates) {
        return this.addressCoordinates;
      }

      const points = [];

      if (this.isDeviceMode) {
        this.devicePoints.forEach((coord) => points.push(coord));
      } else {
        this.vehicleFleetData.forEach((vehicle) => {
          const coord = this.getCoord(vehicle.position);
          if (coord) points.push(coord);
        });
      }

      this.landmarkMarkers.forEach((landmark) => {
        const lat = Number(landmark?.destino_lat);
        const lng = Number(landmark?.destino_long);
        if (!isNaN(lat) && !isNaN(lng)) points.push({ lat, lng });
      });

      if (points.length > 0) {
        const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
        const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
        return { lat: avgLat, lng: avgLng };
      }

      return this.center;
    },

    normalizedRadius() {
      if (!this.radiusKm) return null;
      return Number(this.radiusKm);
    },
  },

  watch: {
    vehicleFleetData: {
      handler(newData) {
        if (!newData || newData.length === 0) return;

        this.preloadVehicleIcons(newData);

        if (this.isDeviceMode) {
          return;
        }

        if (!this.hasFramedOnce) {
          this.hasFramedOnce = true;
          this.scheduleMapViewUpdate();
          return;
        }

        if (this.selectedVehicleId) {
          this.followSelectedVehicle();
        }
      },
      immediate: true,
      deep: true,
    },

    deviceFleet: {
      handler() {
        if (!this.isDeviceMode) {
          return;
        }

        this.$nextTick(() => this.fitBoundsToMarkers());
      },
      deep: true,
    },

    mapMode: {
      handler() {
        this.hasFramedOnce = false;
        this.$nextTick(() => this.fitAll());
      },
    },

    selectedVehicleId: {
      handler(newVehicleId) {
        if (newVehicleId) {
          this.focusOnSelectedVehicle();
        } else {
          this.scheduleMapViewUpdate();
        }
      },
      immediate: true,
    },

    zoomMapa: {
      handler(newZoom) {
        this.currentZoom = newZoom;
      },
      immediate: true,
    },

    addressCoordinates: {
      handler(newCoords, oldCoords) {
        if (newCoords && this.normalizedRadius) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.fitMapToCircle();
            }, 800);
          });
        } else if (newCoords) {
          this.updateMapViewForAddress();
          this.showInfoWindow = "address";
        } else {
          this.scheduleMapViewUpdate();
        }
      },
      immediate: false,
      deep: true,
    },

    landmarkMarkers: {
      handler() {
        this.scheduleMapViewUpdate();
      },
      deep: true,
      immediate: true,
    },

    normalizedRadius: {
      handler(newRadius, oldRadius) {
        if (newRadius && this.addressCoordinates) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.fitMapToCircle();
            }, 800);
          });
        }
      },
      immediate: false,
    },
  },

  methods: {
    scheduleMapViewUpdate(delay = 120) {
      if (this.mapViewTimer) {
        clearTimeout(this.mapViewTimer);
      }

      this.$nextTick(() => {
        this.mapViewTimer = setTimeout(() => {
          this.mapViewTimer = null;
          this.updateMapView();
        }, delay);
      });
    },

    getDevicePosition(device) {
      if (!device.position || !device.position.lat || !device.position.long) {
        return null;
      }

      return {
        lat: parseFloat(device.position.lat),
        lng: parseFloat(device.position.long),
      };
    },

    getDeviceMarkerIcon(device) {
      if (!this.google) {
        return null;
      }

      const url = (device.device_type && device.device_type.file) || GENERIC_PIN;

      return {
        url,
        scaledSize: new this.google.maps.Size(40, 60),
        anchor: new this.google.maps.Point(20, 60),
        origin: new this.google.maps.Point(0, 0),
        labelOrigin: new this.google.maps.Point(20, 74),
      };
    },

    getDeviceLabel(device) {
      if (!this.labelField) {
        return null;
      }

      return {
        text: String(device.label || ""),
        className: "map-pin-label",
        color: "#ffffff",
        fontSize: "11px",
        fontWeight: "600",
      };
    },

    setSelectedDevice(device) {
      this.selectedDevice = device;
      this.$bvModal.show("device-info-modal");
    },

    getVehicleMarkerIcon(vehicle) {
      void this.iconCheckVersion;

      if (!this.google) return null;

      const DEFAULT_ICON = GENERIC_PIN;
      let iconUrl = vehicle?.position?.icon;

      if (
        typeof iconUrl !== "string" ||
        !iconUrl.trim() ||
        this.brokenIcons[iconUrl]
      ) {
        iconUrl = DEFAULT_ICON;
      }

      const entry = this.markerCacheEntry(vehicle);
      const signature = iconUrl + "|" + this.iconCheckVersion;

      if (entry.iconSignature !== signature) {
        entry.iconSignature = signature;
        entry.icon = {
          url: iconUrl,
          scaledSize: new google.maps.Size(40, 60),
          anchor: new google.maps.Point(20, 60),
          origin: new google.maps.Point(0, 0),
          labelOrigin: new google.maps.Point(20, 74),
        };
      }

      return entry.icon;
    },

    markerCacheEntry(vehicle) {
      if (!this._markerCache) this._markerCache = {};

      const key = this.getVehicleKey(vehicle);

      if (!this._markerCache[key]) this._markerCache[key] = {};

      return this._markerCache[key];
    },

    getVehiclePosition(vehicle) {
      const coord = this.getCoord(vehicle?.position);

      if (!coord) return null;

      const entry = this.markerCacheEntry(vehicle);
      const signature = coord.lat + "," + coord.lng;

      if (entry.positionSignature !== signature) {
        entry.positionSignature = signature;
        entry.position = coord;
      }

      return entry.position;
    },

    getVehicleLabel(vehicle) {
      if (!this.labelField) return null;

      const text = this.labelValue(vehicle, this.labelField);

      if (!text) return null;

      const entry = this.markerCacheEntry(vehicle);
      const signature = this.labelField + "|" + text;

      if (entry.labelSignature !== signature) {
        entry.labelSignature = signature;
        entry.label = {
          text: String(text),
          className: "map-pin-label",
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: "600",
        };
      }

      return entry.label;
    },

    labelValue(vehicle, field) {
      const data = vehicle?.vehicle ?? {};

      if (field === "unit") {
        const id = data.unit_id ?? vehicle?.unit_id ?? null;

        return data.unit?.name ?? this.unitNames[id] ?? null;
      }

      return data[field] ?? null;
    },

    loadUnitNames() {
      if (Object.keys(this.unitNames).length) return;

      axios
        .get(`${this.$store.state.api}units`, { params: { paginated: false } })
        .then((response) => {
          const lista = response.data?.data ?? response.data ?? [];

          (Array.isArray(lista) ? lista : []).forEach((unit) => {
            this.$set(this.unitNames, unit.id, unit.name);
          });
        })
        .catch(() => {});
    },

    toggleMapMenu() {
      this.mapMenuOpen = !this.mapMenuOpen;

      if (!this.mapMenuOpen) {
        this.labelMenuOpen = false;
      }
    },

    closeMapMenu() {
      this.mapMenuOpen = false;
      this.labelMenuOpen = false;
    },

    handleDocumentClick(event) {
      if (!this.mapMenuOpen) return;

      const menu = this.$refs.mapMenu;

      if (menu && !menu.contains(event.target)) {
        this.closeMapMenu();
      }
    },

    setLabelField(field) {
      this.labelField = field;
      this.closeMapMenu();

      try {
        if (field) {
          localStorage.setItem(LABEL_FIELD_KEY, field);
        } else {
          localStorage.removeItem(LABEL_FIELD_KEY);
        }
      } catch (error) {}
    },

    loadLabelFieldFromStorage() {
      try {
        const saved = localStorage.getItem(LABEL_FIELD_KEY);
        const known = this.labelOptions.some((o) => o.field === saved);

        if (saved && known) {
          this.labelField = saved;
        }
      } catch (error) {}
    },

    fitAll() {
      this.closeMapMenu();
      this.hasFramedOnce = true;

      if (this.mapViewTimer) {
        clearTimeout(this.mapViewTimer);
      }

      this.$nextTick(() => {
        this.mapViewTimer = setTimeout(() => {
          this.mapViewTimer = null;

          if (this.addressCoordinates && this.normalizedRadius) {
            this.fitMapToCircle();
            return;
          }

          if (this.addressCoordinates) {
            this.updateMapViewForAddress();
            return;
          }

          if (!this.isDeviceMode && this.selectedVehicleId) {
            this.focusOnSelectedVehicle();
            return;
          }

          this.fitBoundsToMarkers();
        }, 120);
      });
    },

    preloadVehicleIcons(list) {
      (list || []).forEach((vehicle) => {
        const url = vehicle?.position?.icon;
        if (typeof url !== "string" || !url.trim()) return;
        if (this.checkedIcons[url]) return;
        this.checkedIcons[url] = true;

        const img = new Image();
        img.onerror = () => {
          this.$set(this.brokenIcons, url, true);
          this.iconCheckVersion++;
        };
        img.src = url;
      });
    },

    getVehicleKey(vehicle) {
      return (
        vehicle?.vehicle_id ||
        vehicle?.vehicle?.id ||
        vehicle?.id ||
        `${vehicle?.position?.lat || 0}-${vehicle?.position?.lng || 0}`
      );
    },

    getLandmarkIcon(landmark) {
      if (!this.google) return null;

      let iconUrl = landmark?.landmark_type?.file;
      if (typeof iconUrl !== "string" || !iconUrl.trim()) {
        iconUrl = GENERIC_PIN;
      }
      return {
        url: iconUrl,
        scaledSize: new google.maps.Size(30, 45),
        anchor: new google.maps.Point(15, 45),
        origin: new google.maps.Point(0, 0),
      };
    },

    onLandmarkMarkerClick(landmark) {
      this.selectedLandmark = landmark;
      this.showInfoWindow = "landmark";
    },

    closeInfoWindow() {
      this.showInfoWindow = null;
      this.selectedVehicleDetails = null;
      this.selectedLandmark = {};
    },

    getVehicleIcon(vehicle) {
      if (!this.google) return null;

      if (vehicle.vehicle_image_url) {
        return {
          url: vehicle.vehicle_image_url,
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16),
        };
      }

      return {
        url: require("@/assets/images/icons/ambulance.png"),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
        origin: new google.maps.Point(0, 0),
      };
    },

    getAddressIcon() {
      if (!this.google) return null;

      return {
        url: require("@/assets/images/pin/map-pins-rescue.png"),
        scaledSize: new google.maps.Size(32, 40),
        anchor: new google.maps.Point(16, 40),
        origin: new google.maps.Point(0, 0),
      };
    },

    getCircleOptions() {
      return {
        strokeColor: "#4285F4",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#4285F4",
        fillOpacity: 0.15,
        clickable: false,
        zIndex: 1,
      };
    },

    showAddressInfo() {
      this.showInfoWindow = null;
      this.$nextTick(() => {
        this.showInfoWindow = "address";
      });
    },

    getMapPoints() {
      const points = [];

      if (this.isDeviceMode) {
        this.devicePoints.forEach((coord) => points.push(coord));
      } else {
        this.vehicleFleetData.forEach((vehicle) => {
          const coord = this.getCoord(vehicle.position);
          if (coord) points.push(coord);
        });
      }

      this.landmarkMarkers.forEach((landmark) => {
        const lat = Number(landmark?.destino_lat);
        const lng = Number(landmark?.destino_long);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push({ lat, lng });
        }
      });

      if (this.addressCoordinates) {
        const lat = Number(this.addressCoordinates.lat);
        const lng = Number(this.addressCoordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push({ lat, lng });
        }
      }

      return points;
    },

    // getCorePoints mantido caso queira usar em outro contexto,
    // mas NÃO é mais usado no fitBoundsToMarkers
    getCorePoints(points) {
      if (!points || points.length < 6) return points;

      const median = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
          return (sorted[mid - 1] + sorted[mid]) / 2;
        }
        return sorted[mid];
      };

      const centerLat = median(points.map((p) => p.lat));
      const centerLng = median(points.map((p) => p.lng));

      const distanceScore = (point) => {
        const dLat = point.lat - centerLat;
        const dLng = point.lng - centerLng;
        return Math.sqrt(dLat * dLat + dLng * dLng);
      };

      const withDistance = points
        .map((point) => ({ point, score: distanceScore(point) }))
        .sort((a, b) => a.score - b.score);

      const keepCount = Math.max(Math.ceil(points.length * 0.8), 4);
      return withDistance.slice(0, keepCount).map((item) => item.point);
    },

    onCircleChanged() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.fitMapToCircle();
        }, 300);
      });
    },

    fitMapToCircle() {
      if (
        !this.addressCoordinates ||
        !this.normalizedRadius ||
        !this.$refs.mapRef
      ) {
        return;
      }

      this.$refs.mapRef.$mapPromise
        .then((map) => {
          const bounds = new google.maps.LatLngBounds();

          const center = new google.maps.LatLng(
            this.addressCoordinates.lat,
            this.addressCoordinates.lng
          );
          bounds.extend(center);

          const earthRadiusKm = 6371;
          const radiusInDegrees =
            (this.normalizedRadius / earthRadiusKm) * (180 / Math.PI);
          const lngCorrection = Math.cos(
            (this.addressCoordinates.lat * Math.PI) / 180
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat + radiusInDegrees,
              this.addressCoordinates.lng
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat - radiusInDegrees,
              this.addressCoordinates.lng
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat,
              this.addressCoordinates.lng + radiusInDegrees / lngCorrection
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat,
              this.addressCoordinates.lng - radiusInDegrees / lngCorrection
            )
          );

          map.fitBounds(bounds, {
            top: 60,
            right: 60,
            bottom: 60,
            left: 60,
          });

          const zoomListener = google.maps.event.addListener(
            map,
            "idle",
            () => {
              const currentZoom = map.getZoom();
              if (currentZoom > 15) {
                map.setZoom(15);
              }
              google.maps.event.removeListener(zoomListener);
            }
          );
        })
        .catch((error) => {
          console.error("❌ Error fitting bounds:", error);
        });
    },

    updateMapViewForAddress() {
      if (!this.addressCoordinates) return;

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.mapRef) {
            this.$refs.mapRef.$mapPromise.then((map) => {
              if (this.normalizedRadius) {
                this.fitMapToCircle();
              } else {
                map.setCenter(this.addressCoordinates);
                map.setZoom(15);
              }
            });
          }
        }, 300);
      });
    },

    creatCenter(coord) {
      if (this.center.lat == 0 && coord && !this.addressCoordinates) {
        this.center = coord;
      }
    },

    getCoord(position) {
      if (
        position &&
        (position.lat || position.latitude || position.destino_lat) &&
        (position.lng || position.lon || position.longitude || position.destino_long)
      ) {
        const lat = parseFloat(
          position.lat || position.latitude || position.destino_lat
        );
        const lng = parseFloat(
          position.lng || position.lon || position.longitude || position.destino_long
        );
        if (!isNaN(lat) && !isNaN(lng)) {
          const coord = { lat, lng };
          this.creatCenter(coord);
          return coord;
        }
      }
      return null;
    },

    getSelectedVehicleCoord() {
      if (!this.selectedVehicleId || !this.vehicleFleetData.length) return null;

      const selectedVehicle = this.vehicleFleetData.find(
        (vehicle) =>
          vehicle.vehicle?.id === this.selectedVehicleId ||
          vehicle.vehicle_id === this.selectedVehicleId
      );

      if (!selectedVehicle || !selectedVehicle.position) return null;

      return this.getCoord(selectedVehicle.position);
    },

    focusOnSelectedVehicle() {
      const coord = this.getSelectedVehicleCoord();

      if (!coord || !this.$refs.mapRef) return;

      this.$refs.mapRef.$mapPromise.then((map) => {
        map.setCenter(coord);
        map.setZoom(18);
        this.center = coord;
        this.mapCenter = coord;
        this.currentZoom = 18;
      });
    },

    followSelectedVehicle() {
      const coord = this.getSelectedVehicleCoord();

      if (!coord || !this.$refs.mapRef) return;

      this.$refs.mapRef.$mapPromise.then((map) => {
        map.setCenter(coord);
        this.center = coord;
        this.mapCenter = coord;
      });
    },

    fitBoundsToMarkers() {
      if (!this.$refs.mapRef) return;

      this.$refs.mapRef.$mapPromise.then((map) => {
        const points = this.getMapPoints();

        if (!points.length) return;

        if (points.length === 1) {
          map.setCenter(points[0]);
          map.setZoom(Math.min(this.zoomMapa, 14));
          return;
        }

        const bounds = new google.maps.LatLngBounds();

        // ✅ USA TODOS os pontos — sem filtro getCorePoints
        points.forEach((coord) => {
          bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, {
            top: 60,
            right: 60,
            bottom: 60,
            left: 60,
          });

          google.maps.event.addListenerOnce(map, "idle", () => {
            const currentZoom = map.getZoom();
            // Zoom mínimo razoável para não ficar muito afastado
            if (currentZoom < 5) map.setZoom(5);
            // ✅ Sem cap máximo — fitBounds decide o zoom ideal
          });
        }
      });
    },

    updateMapView() {
      if (!this.autoFitMap) return;
      if (this.addressCoordinates && this.normalizedRadius) {
        this.fitMapToCircle();
      } else if (this.addressCoordinates) {
        this.updateMapViewForAddress();
      } else if (this.selectedVehicleId) {
        this.focusOnSelectedVehicle();
      } else {
        this.fitBoundsToMarkers();
      }
    },

    setSelectedVehicleDetails(vehicle) {
      const position = vehicle.position ?? {};
      const vehicleCoord = this.getCoord(position);

      this.markerPosition = vehicleCoord;
      this.vehicleDatalhe = vehicle;

      if (vehicleCoord && this.$refs.mapRef) {
        this.$refs.mapRef.$mapPromise.then((map) => {
          map.setCenter(vehicleCoord);
          setTimeout(() => {
            map.setZoom(18);
          }, 100);
        });
      }

      this.selectedVehicleDetails = {
        BackBattery: position.BackBattery ?? 0,
        CPRDateTime: position.CPRDateTime ?? "",
        lat: position.lat ?? 0,
        lon: position.lng ?? 0,
        speed: position.speed ?? 0,
        ignition_status: position.ignition_status ?? 0,
        port_status: position.port_status ?? 0,
        siren_status: position.siren_status ?? 0,
        device_battery_level: position.device_battery_level ?? 0,
      };

      this.$bvModal.show("vehicle-info-modal");
    },

    handleZoomChanged(newZoom) {
      this.currentZoom = newZoom;
      this.$emit("zoomChanged", newZoom);
    },

    init() {
      this.$emit("reload", true);
    },

    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.closeMapMenu();
        return;
      }

      if (event.key === "r") {
        this.init();
      }
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("click", this.handleDocumentClick);
    this.loadLabelFieldFromStorage();
    this.loadUnitNames();
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("click", this.handleDocumentClick);

    if (this.mapViewTimer) {
      clearTimeout(this.mapViewTimer);
      this.mapViewTimer = null;
    }
  },

  components: {
    RefreshCcwIcon,
    CurrentLocationMap,
    VehicleInfo,
    DeviceInfo,
  },
};
</script>

<style scoped>
.map-shell {
  position: relative;
}

.map-menu {
  position: absolute;
  top: 60px;
  right: 10px;
  z-index: 5;
}

.map-menu-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #d7dee3;
  border-radius: 3px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.25);
  color: #33424d;
  font-size: 12px;
  font-weight: 600;
  padding: 7px 11px;
  cursor: pointer;
}

.map-menu-toggle:hover {
  background: #f2f6f8;
}

.map-menu-toggle.is-on {
  background: #0e6a76;
  border-color: #0e6a76;
  color: #ffffff;
}

.map-menu-toggle:focus-visible {
  outline: 2px solid #0e6a76;
  outline-offset: 2px;
}

.map-menu-text {
  white-space: nowrap;
}

.map-menu-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 190px;
  background: #ffffff;
  border: 1px solid #d7dee3;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}

.map-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: 0;
  border-bottom: 1px solid #eef2f4;
  color: #33424d;
  font-size: 12.5px;
  font-weight: 600;
  padding: 9px 12px;
  text-align: left;
  cursor: pointer;
}

.map-menu-item:last-child {
  border-bottom: 0;
}

.map-menu-item:hover {
  background: #f2f6f8;
}

.map-menu-item.is-on {
  color: #0e6a76;
}

.map-menu-caret {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.7;
}

.map-menu-sub {
  background: #f7fafb;
  border-bottom: 1px solid #eef2f4;
}

.map-menu-sub:last-child {
  border-bottom: 0;
}

.map-menu-sub-item {
  display: block;
  width: 100%;
  background: none;
  border: 0;
  border-bottom: 1px solid #eef2f4;
  color: #33424d;
  font-size: 12.5px;
  padding: 8px 12px 8px 32px;
  text-align: left;
  cursor: pointer;
}

.map-menu-sub-item:last-child {
  border-bottom: 0;
}

.map-menu-sub-item:hover {
  background: #eaf1f3;
}

.map-menu-sub-item.is-active {
  background: #0e6a76;
  color: #ffffff;
}

.map-menu-sub-item.is-clear {
  border-top: 1px solid #d7dee3;
  color: #a4322c;
  font-weight: 600;
}

.address-info {
  max-width: 250px;
  font-family: "Roboto", sans-serif;
}

.address-info h6 {
  color: #1976d2;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 5px;
}

.address-info .badge {
  font-size: 0.75rem;
}
</style>
<style>
/* Nao escopado de proposito: o Google renderiza esta div fora da arvore do Vue. */
.map-pin-label {
  background: rgba(23, 33, 41, 0.88);
  border-radius: 3px;
  padding: 2px 6px;
  white-space: nowrap;
  letter-spacing: 0.01em;
  text-shadow: none;
}
</style>
